import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCopyToClipboard } from '@/utils/copyToClipboard';
import { getUrlWithoutHash } from '@/utils/url/getUrlWithoutHash';

import { Modal } from '../Modal/Modal';

import { shareProfileStyles as sx } from './Share.styles';

const TIMEOUT_THRESHOLD = 1250;

type Props = {
	open: boolean;
	onClose: () => void;
	title: string;
	description: string;
};

export const ShareModal = ({ open, onClose, title, description }: Props) => {
	const { t } = useTranslation();
	const { copyToClipboard } = useCopyToClipboard(false);
	const [isCopyPending, setIsCopyPending] = useState(false);
	const [wasOpen, setWasOpen] = useState(open);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	if (open !== wasOpen) {
		setWasOpen(open);

		if (open) {
			setIsCopyPending(false);
		}
	}

	const handleClose = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		setIsCopyPending(false);
		onClose();
	};

	const handleShareClick = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		copyToClipboard(getUrlWithoutHash());
		setIsCopyPending(true);

		timeoutRef.current = setTimeout(() => {
			setIsCopyPending(false);
			timeoutRef.current = null;
		}, TIMEOUT_THRESHOLD);
	};

	return (
		<Modal open={open} onClose={handleClose} title={title}>
			<Box sx={sx.urlContainer}>
				<Box sx={sx.urlText}>{getUrlWithoutHash()}</Box>
				<Button
					variant='contained'
					sx={sx.copyButton}
					onClick={handleShareClick}
					disabled={isCopyPending}
				>
					{isCopyPending ? t('Copied to clipboard') : t('Copy Link')}
				</Button>
			</Box>
			<Typography id='modal-description' sx={sx.info}>
				{description}
			</Typography>
		</Modal>
	);
};
